import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, Circle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const phases = [
  {
    phase: 'Phase 1',
    title: 'Blockchain Development',
    status: 'completed',
    items: ['Core blockchain architecture', 'PoA consensus implementation', 'Testnet launch'],
  },
  {
    phase: 'Phase 2',
    title: 'Wallet Launch',
    status: 'completed',
    items: ['Polk Wallet development', 'Multi-chain support', 'Mobile app release'],
  },
  {
    phase: 'Phase 3',
    title: 'Polk Swap Release',
    status: 'in-progress',
    items: ['DEX smart contracts', 'Liquidity pools', 'Trading interface'],
  },
  {
    phase: 'Phase 4',
    title: 'PUSD Stablecoin Launch',
    status: 'upcoming',
    items: ['Stablecoin mechanism', 'Collateral system', 'Integration with DEX'],
  },
  {
    phase: 'Phase 5',
    title: 'DApps Ecosystem',
    status: 'upcoming',
    items: ['Developer SDK', 'Grant program', 'First DApps onboarding'],
  },
  {
    phase: 'Phase 6',
    title: 'Global Adoption',
    status: 'upcoming',
    items: ['Enterprise partnerships', 'Cross-chain bridges', 'Mainstream adoption'],
  },
];

export const Roadmap = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'in-progress':
        return 'bg-blue-500 animate-pulse';
      default:
        return 'bg-zinc-600';
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') {
      return <Check className="w-4 h-4 text-white" />;
    }
    return <Circle className="w-3 h-3 text-white" />;
  };

  return (
    <section
      id="roadmap"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#F1F5F9]'}`}
      ref={ref}
      data-testid="roadmap-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            Roadmap
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Our <span className="gradient-text">Journey</span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            A clear path to building the future of decentralized finance.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/50 via-blue-500/20 to-transparent -translate-x-1/2" />

          <div className="space-y-12">
            {phases.map((phase, index) => (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className={`relative flex flex-col lg:flex-row gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div
                    className={`inline-block p-8 rounded-3xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                      isDark 
                        ? 'bg-white/[0.03] border-white/10' 
                        : 'bg-white border-black/5'
                    } ${index % 2 === 0 ? 'lg:ml-auto' : 'lg:mr-auto'}`}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400">
                        {phase.phase}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          phase.status === 'completed'
                            ? 'bg-green-500/20 text-green-400'
                            : phase.status === 'in-progress'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-zinc-500/20 text-zinc-400'
                        }`}
                      >
                        {phase.status === 'in-progress' ? 'In Progress' : phase.status === 'completed' ? 'Completed' : 'Upcoming'}
                      </span>
                    </div>
                    <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {phase.title}
                    </h3>
                    <ul className={`space-y-2 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                      {phase.items.map((item) => (
                        <li
                          key={item}
                          className={`flex items-center gap-2 ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}
                          style={{ justifyContent: index % 2 === 0 ? 'flex-end' : 'flex-start' }}
                        >
                          {index % 2 !== 0 && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                          <span>{item}</span>
                          {index % 2 === 0 && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Timeline dot */}
                <div className={`hidden lg:flex absolute left-1/2 -translate-x-1/2 w-10 h-10 rounded-full items-center justify-center border-4 ${
                  isDark ? 'border-[#0a0a0a]' : 'border-[#F1F5F9]'
                }`}
                  style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                >
                  <div className={`w-10 h-10 rounded-full ${getStatusColor(phase.status)} flex items-center justify-center`}>
                    {getStatusIcon(phase.status)}
                  </div>
                </div>

                {/* Empty space for alternating layout */}
                <div className="hidden lg:block flex-1" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
